import type { VesselMetricsPayload } from '@ankor-io/common/types/lighthouse'

export type FunnelStage = {
  id: string
  label: string
  value: number
  conversionLabel?: string
  gradientFrom: string
  gradientTo: string
}

// The funnel deliberately stops at cardsSetup. `cardsSpent` is part of
// VesselMetricsPayload but is not rendered as a funnel stage yet (product has
// not defined where the "cards spent" step sits in the onboarding funnel). The
// `buildFunnelStages` length assertion in the tests is load-bearing: if a sixth
// stage is added here, that test must be updated, which is the signal that the
// omission was intentional rather than forgotten.
const FUNNEL_STAGE_DEFINITIONS = [
  {
    id: 'vessels',
    label: 'VESSELS',
    metric: 'vessels',
    gradientFrom: '#1e3a8a',
    gradientTo: '#1e40af',
  },
  {
    id: 'kyc-submitted',
    label: 'KYC SUBMITTED',
    metric: 'kycSubmitted',
    gradientFrom: '#2563eb',
    gradientTo: '#3b82f6',
  },
  {
    id: 'kyc-approved',
    label: 'KYC APPROVED',
    metric: 'kycApproved',
    gradientFrom: '#4f46e5',
    gradientTo: '#6366f1',
  },
  {
    id: 'funds-deposited',
    label: 'FUNDS DEPOSITED',
    metric: 'fundsDeposited',
    gradientFrom: '#7c3aed',
    gradientTo: '#8b5cf6',
  },
  {
    id: 'cards-setup',
    label: 'CARDS SETUP',
    metric: 'cardsSetup',
    gradientFrom: '#9333ea',
    gradientTo: '#a855f7',
  },
] as const satisfies ReadonlyArray<{
  id: string
  label: string
  metric: keyof VesselMetricsPayload
  gradientFrom: string
  gradientTo: string
}>

function conversionLabelFromPreviousStage(current: number, previous: number) {
  if (previous === 0) {
    return undefined
  }

  return `${((current / previous) * 100).toFixed(1)}% of previous stage`
}

export const buildFunnelStages = (metrics: VesselMetricsPayload): FunnelStage[] => {
  return FUNNEL_STAGE_DEFINITIONS.map((definition, index) => {
    const value = metrics[definition.metric]
    const previousMetric = index > 0 ? FUNNEL_STAGE_DEFINITIONS[index - 1].metric : null
    const previousValue = previousMetric ? metrics[previousMetric] : 0

    return {
      id: definition.id,
      label: definition.label,
      value,
      gradientFrom: definition.gradientFrom,
      gradientTo: definition.gradientTo,
      conversionLabel: index === 0 ? undefined : conversionLabelFromPreviousStage(value, previousValue),
    }
  })
}
